import React from "react";
import {
  MapPin,
  Search,
  X,
  Calendar,
  User,
  Building,
  Info,
  Navigation,
  ClipboardList,
} from "lucide-react";
import { Button } from "../../components/AdminSharedComponents";
import TextField from "../../components/ui/Form/TextField";
import DropdownField from "../../components/ui/Form/DropdownField";
import PageFormHeader from "../../components/PageFormHeader";

const ProjectFormComponent = ({
  formId,
  isEdit,
  loading,
  submitting,
  submitIcon,
  title,
  backLink,
  backText,
  cancelLink,
  cancelText,
  submitText,
  loadingText,
  formData,
  manualCoordinates,
  handleChange,
  handleManualCoordinateChange,
  applyManualCoordinates,
  clearCoordinates,
  handleLocationSearch,
  locationSearching,
  mapRef,
  handleSubmit,
}) => {
  return (
    <div className="min-h-screen bg-gray-50">
      <PageFormHeader
        title={title}
        backLink={backLink}
        backText={backText}
        cancelLink={cancelLink}
        cancelText={cancelText}
        submitText={submitText}
        loadingText={loadingText}
        submitIcon={submitIcon}
        loading={submitting || loading}
        formId={formId}
      />

      <div className="px-4 md:px-6 py-6 md:py-8 pb-24 md:pb-8">
        <div className="max-w-7xl mx-auto">
          <form id={formId} onSubmit={handleSubmit}>
            <div className="space-y-8">
              {/* Project Information */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="flex items-center gap-2 text-lg font-semibold text-primary-dark">
                    <ClipboardList className="h-5 w-5" />
                    Project Information
                  </h2>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <TextField
                        label="Project Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter project name"
                        required
                      />
                    </div>

                    <div>
                      <TextField
                        label={
                          <span className="flex items-center gap-1">
                            <User className="h-4 w-4" />
                            Person In Charge (PIC)
                          </span>
                        }
                        name="pic"
                        value={formData.pic}
                        onChange={handleChange}
                        placeholder="Name of the person in charge"
                      />
                    </div>

                    <div>
                      <DropdownField
                        label="Project Status"
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                        options={[
                          { value: "In Progress", label: "In Progress" },
                          { value: "Completed", label: "Completed" },
                          { value: "On Hold", label: "On Hold" },
                          { value: "Cancelled", label: "Cancelled" },
                        ]}
                      />
                    </div>

                    <div>
                      <TextField
                        label={
                          <span className="flex items-center gap-1">
                            <Building className="h-4 w-4" />
                            Client Name
                          </span>
                        }
                        name="client_name"
                        value={formData.client_name}
                        onChange={handleChange}
                        placeholder="Enter client name"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <TextField
                        label={
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            Start Date
                          </span>
                        }
                        type="date"
                        name="start_date"
                        value={formData.start_date}
                        onChange={handleChange}
                      />
                      <TextField
                        label={
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            End Date
                          </span>
                        }
                        type="date"
                        name="end_date"
                        value={formData.end_date}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Project Location */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="flex items-center gap-2 text-lg font-semibold text-primary-dark">
                    <MapPin className="h-5 w-5" />
                    Project Location
                  </h2>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-8 gap-8">
                    <div className="lg:col-span-3 space-y-6 order-2 lg:order-1">
                      <div>
                        <label className="flex items-center gap-1 text-sm font-medium mb-2 text-primary-dark">
                          Project Location
                        </label>
                        <div className="flex gap-3">
                          <TextField
                            name="location"
                            value={formData.location}
                            onChange={handleChange}
                            placeholder="Enter project location or address"
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            onClick={handleLocationSearch}
                            loading={locationSearching}
                            icon={Search}
                          >
                            {locationSearching ? "Searching..." : "Search"}
                          </Button>
                        </div>
                      </div>

                      <div className="bg-primary/5 rounded-xl border border-primary/20 overflow-hidden">
                        <div className="p-5 space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="text-base font-semibold text-primary-dark">
                              Project Coordinates
                            </h4>
                            {formData.coordinates && (
                              <Button
                                type="button"
                                variant="secondary"
                                className="text-xs px-3 py-1.5 h-auto"
                                onClick={clearCoordinates}
                                icon={X}
                              >
                                Clear Location
                              </Button>
                            )}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <TextField
                              label="Latitude"
                              name="lat"
                              type="number"
                              step="any"
                              value={manualCoordinates.lat}
                              onChange={handleManualCoordinateChange}
                              placeholder="e.g., -7.250445"
                            />
                            <TextField
                              label="Longitude"
                              name="lng"
                              type="number"
                              step="any"
                              value={manualCoordinates.lng}
                              onChange={handleManualCoordinateChange}
                              placeholder="e.g., 112.768845"
                            />
                          </div>

                          <Button
                            type="button"
                            variant="secondary"
                            onClick={applyManualCoordinates}
                            className="w-full"
                            icon={Navigation}
                          >
                            {formData.coordinates
                              ? "Update Coordinates"
                              : "Apply Coordinates"}
                          </Button>
                        </div>
                      </div>

                      <div className="flex items-start gap-2 text-sm text-gray-600 bg-gray-50 p-4 rounded-lg">
                        <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p>
                            <strong>How to set location:</strong>
                          </p>
                          <ul className="list-disc list-inside ml-2">
                            <li>Click anywhere on the map</li>
                            <li>Use the search box above</li>
                            <li>Enter coordinates manually</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="lg:col-span-5 order-1 lg:order-2">
                      <div className="border border-gray-300 rounded-lg overflow-hidden shadow-inner h-full">
                        <div
                          ref={mapRef}
                          className="w-full h-[300px] md:h-full rounded-lg"
                          style={{ zIndex: 0 }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProjectFormComponent;
